import {
    createContext,
    ReactNode,
    use,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useLocation } from "react-router-dom";
import useAnalytics from "@/services/analytics/useAnalytics.ts";
import {
    AuditLinkClosed,
    AuditProspect,
    auditTokenOf,
    fetchAuditProspect,
} from "./auditProspect.ts";

type AuditProspectState =
    | { status: "loading" }
    | { status: "ready"; prospect: AuditProspect }
    /** Unknown, expired or never valid. */
    | { status: "closed" }
    | { status: "failed"; message: string };

type AuditProspectValue = {
    /** The token in the url; null on every screen that is not an audit link. */
    token: string | null;
    /**
     * The link this visit came in on, kept on the screens after it: the code
     * step of the sign-up shows the same profile the form did.
     */
    openedToken: string | null;
    state: AuditProspectState;
    /** Takes the answer of a save or an opt-out, so every part of the page shows it. */
    update: (prospect: AuditProspect) => void;
};

const AuditProspectContext = createContext<AuditProspectValue>({
    token: null,
    openedToken: null,
    state: { status: "loading" },
    update: () => undefined,
});

/**
 * Reads the prospect behind an audit link once, for the form, the profile
 * beside it and the editor. Mounted around every signed-out screen; on any
 * other path it fetches nothing.
 */
export function AuditProspectProvider({
    children,
    preview = false,
}: {
    children: ReactNode;
    /** The viewer is not the prospect, so the open is not recorded. */
    preview?: boolean;
}) {
    const token = auditTokenOf(useLocation().pathname);
    const [openedToken, setOpenedToken] = useState(token);
    if (token !== null && token !== openedToken) {
        setOpenedToken(token);
    }
    const [state, setState] = useState<AuditProspectState>({
        status: "loading",
    });
    const { trackAuditLinkOpenedEvent } = useAnalytics();

    useEffect(() => {
        if (token === null) {
            return;
        }
        // The landing screen and the editor share a token, so the state a
        // token change would have to reset never outlives its own link.
        let current = true;
        fetchAuditProspect(token, preview).then(
            (prospect) => {
                if (!current) {
                    return;
                }
                setState({ status: "ready", prospect });
                // The same open the row records: a preview is not the prospect's.
                if (!preview) {
                    trackAuditLinkOpenedEvent({ domain: prospect.domain });
                }
            },
            (error: unknown) =>
                current &&
                setState(
                    error instanceof AuditLinkClosed
                        ? { status: "closed" }
                        : {
                              status: "failed",
                              message:
                                  error instanceof Error
                                      ? error.message
                                      : "Something went wrong.",
                          },
                ),
        );

        return () => {
            current = false;
        };
    }, [token, preview, trackAuditLinkOpenedEvent]);

    const update = useCallback(
        (prospect: AuditProspect) => setState({ status: "ready", prospect }),
        [],
    );
    const value = useMemo(
        () => ({ token, openedToken, state, update }),
        [token, openedToken, state, update],
    );

    return (
        <AuditProspectContext value={value}>{children}</AuditProspectContext>
    );
}

export function useAuditProspect(): AuditProspectValue {
    return use(AuditProspectContext);
}
